import {
  Home, Truck, Sparkles, Building2, Bath, Flame, Droplets, Trash2, Refrigerator,
  Briefcase, Store, FileSignature, Coffee, Dumbbell,
} from "lucide-react";
import { PRICING, FAQS } from "@/data/content";

// ---- Category card lists (used on /services and homepage) ----
export const RESIDENTIAL_CARDS = [
  { name: "End of Tenancy Cleaning", short: "Complete clean of an empty rental before you hand back the keys.", href: "/end-of-tenancy-cleaning-dublin", icon: Home },
  { name: "Move-Out Cleaning", short: "Deep clean for tenants moving from a house or apartment.", href: "/move-out-cleaning-dublin", icon: Truck },
  { name: "Deep Cleaning", short: "Intensive cleaning for homes and rental properties needing extra attention.", href: "/deep-cleaning-dublin", icon: Sparkles },
  { name: "Apartment Cleaning", short: "Studios, 1, 2 and 3-bedroom apartments of all sizes.", href: "/apartment-cleaning-dublin", icon: Building2 },
  { name: "House Cleaning", short: "Detailed cleaning for 2-bedroom houses and larger.", href: "/house-cleaning-dublin", icon: Home },
  { name: "Landlord Cleaning", short: "Cleaning between tenants for landlords and letting agents.", href: "/landlord-cleaning-dublin", icon: Building2 },
  { name: "Oven Cleaning", short: "Internal oven, racks and trays — grease and burnt-on residue.", href: "/oven-cleaning-dublin", icon: Flame },
  { name: "Carpet Cleaning", short: "Bedrooms, stairs, halls and living rooms, priced by size.", href: "/carpet-cleaning-dublin", icon: Droplets },
  { name: "Rubbish & Furniture Removal", short: "Unwanted bags, boxes, household items and furniture.", href: "/rubbish-removal-dublin", icon: Trash2 },
];

export const COMMERCIAL_CARDS = [
  { name: "Office Cleaning", short: "One-off, regular and move-in/out office cleaning.", href: "/office-cleaning-dublin", icon: Briefcase },
  { name: "Commercial Cleaning", short: "Shops, cafés, salons, gyms, clinics and business premises.", href: "/commercial-cleaning-dublin", icon: Store },
  { name: "Business Cleaning Contracts", short: "Daily, weekly or customised recurring cleaning contracts.", href: "/business-cleaning-contracts-dublin", icon: FileSignature },
  { name: "Retail & Shop Cleaning", short: "Clean, presentable retail spaces for staff and customers.", href: "/commercial-cleaning-dublin", icon: Store },
  { name: "Cafés, Salons, Gyms & More", short: "Cleaning for cafés, salons, barbers, gyms and other premises.", href: "/commercial-cleaning-dublin", icon: Coffee },
];

export const DEFAULT_SERVICE_FAQS = [
  { q: "How do I get a price?", a: "Send photos or a short walkthrough video with the property size and location. We confirm a clear price, availability and estimated time before you book." },
  { q: "Is the price fixed?", a: "The price is confirmed after we see accurate details, photos or a video. Extra work is never added without your agreement." },
  { q: "Do you bring products and equipment?", a: "Yes. Our team brings normal cleaning products and equipment unless something specialist is required." },
  { q: "Which areas do you cover?", a: "Dublin, County Kildare and surrounding areas, subject to availability." },
];

// ---- Individual service page content ----
export const SERVICE_PAGES = {
  "end-of-tenancy-cleaning-dublin": {
    category: "residential", icon: Home, name: "End of Tenancy Cleaning",
    h1: "End of Tenancy Cleaning Dublin",
    metaTitle: "End of Tenancy Cleaning Dublin | GoCleaning Dublin",
    metaDescription: "End of tenancy cleaning in Dublin for tenants, landlords and letting agents. Apartments and houses cleaned to inspection standard. Send photos for a fast quote.",
    intro: "Professional end of tenancy cleaning in Dublin for tenants, landlords and letting agents. We clean apartments and houses to a professional standard, ready for handover and inspection.",
    sections: [
      { h2: "Who we clean for", items: ["Tenants preparing to hand back the keys", "Landlords getting a property ready for new tenants", "Letting agents and property managers arranging handovers"] },
      { h2: "What we cover", items: ["Kitchens — worktops, sink, cupboards, hob and appliance exteriors", "Bathrooms — toilet, shower, bath, tiles and light limescale", "Bedrooms and living areas — dusting, skirting, sockets, floors", "General areas — hallways, stairs, cobweb removal and internal doors"] },
      { h2: "Optional extras", items: ["Internal oven cleaning", "Fridge and freezer cleaning", "Carpet cleaning by room", "Interior and exterior window cleaning"] },
    ],
    showPricing: "both",
    prefillServices: ["End of tenancy cleaning"],
    related: ["move-out-cleaning-dublin", "deep-cleaning-dublin", "apartment-cleaning-dublin", "house-cleaning-dublin"],
    faqs: FAQS,
  },
  "move-out-cleaning-dublin": {
    category: "residential", icon: Truck, name: "Move-Out Cleaning",
    h1: "Move-Out Cleaning Dublin",
    metaTitle: "Move-Out Cleaning Dublin | GoCleaning Dublin",
    metaDescription: "Move-out cleaning in Dublin before handing back the keys. Empty or partially furnished apartments and houses, with optional rubbish and furniture removal. Get a quote.",
    intro: "Move-out cleaning in Dublin for tenants leaving a house or apartment. We clean before you hand back the keys, whether the property is empty or partially furnished.",
    sections: [
      { h2: "Ideal for", items: ["Cleaning before handing back the keys", "Empty and partially furnished properties", "Apartments and houses of all sizes"] },
      { h2: "Optional add-ons", items: ["Rubbish removal — bags, boxes and household items", "Furniture removal — quoted after photos", "Oven, fridge and carpet cleaning"] },
    ],
    showPricing: "both",
    prefillServices: ["Move-out cleaning"],
    related: ["end-of-tenancy-cleaning-dublin", "rubbish-removal-dublin", "deep-cleaning-dublin"],
  },
  "deep-cleaning-dublin": {
    category: "residential", icon: Sparkles, name: "Deep Cleaning",
    h1: "Deep Cleaning Dublin",
    metaTitle: "Deep Cleaning Dublin | GoCleaning Dublin",
    metaDescription: "Deep cleaning in Dublin for homes and rental properties. Kitchens, bathrooms, floors, skirting boards and accessible surfaces. Heavy cleaning quoted after photos.",
    intro: "Intensive deep cleaning in Dublin for homes and rental properties that need extra attention. We focus on detail across kitchens, bathrooms and living spaces.",
    sections: [
      { h2: "What deep cleaning covers", items: ["Kitchens and bathrooms in detail", "Floors, skirting boards and doors", "Accessible surfaces and fittings", "Cobweb removal and reachable areas"] },
      { h2: "Heavy cleaning", items: ["Very dirty or neglected properties are quoted after photos or a short video", "We confirm the scope and price before starting", "No work is added without your approval"] },
    ],
    showPricing: "both",
    prefillServices: ["Deep cleaning"],
    related: ["end-of-tenancy-cleaning-dublin", "house-cleaning-dublin", "oven-cleaning-dublin"],
  },
  "apartment-cleaning-dublin": {
    category: "residential", icon: Building2, name: "Apartment Cleaning",
    h1: "Apartment Cleaning Dublin",
    metaTitle: "Apartment Cleaning Dublin | GoCleaning Dublin",
    metaDescription: "Apartment cleaning in Dublin for studios and 1, 2 and 3-bedroom apartments. Move-out, landlord and deep cleaning with clear starting prices. Send photos for a quote.",
    intro: "Professional apartment cleaning in Dublin for studios and apartments of all sizes. Choose move-out, landlord or deep cleaning to suit your situation.",
    sections: [
      { h2: "Apartments we clean", items: ["Studio apartments", "One-bedroom apartments", "Two-bedroom apartments", "Three-bedroom apartments"] },
      { h2: "Cleaning options", items: ["Move-out cleaning", "Landlord cleaning between tenants", "Deep cleaning for extra attention"] },
    ],
    showPricing: "apartments",
    prefillServices: [],
    related: ["end-of-tenancy-cleaning-dublin", "deep-cleaning-dublin", "landlord-cleaning-dublin"],
  },
  "house-cleaning-dublin": {
    category: "residential", icon: Home, name: "House Cleaning",
    h1: "House Cleaning Dublin",
    metaTitle: "House Cleaning Dublin | GoCleaning Dublin",
    metaDescription: "House cleaning in Dublin for 2, 3, 4-bedroom and larger houses. End-of-tenancy and deep cleaning with optional extras and clear starting prices. Get a quote.",
    intro: "Detailed house cleaning in Dublin for houses with two or more bedrooms. Ideal for end-of-tenancy handovers and deep cleaning.",
    sections: [
      { h2: "Houses we clean", items: ["Two-bedroom houses", "Three-bedroom houses", "Four-bedroom houses", "Larger properties — quoted after photos or video"] },
      { h2: "Cleaning and extras", items: ["End-of-tenancy cleaning", "Deep cleaning", "Optional oven, fridge, carpet and window cleaning"] },
    ],
    showPricing: "houses",
    prefillServices: [],
    related: ["end-of-tenancy-cleaning-dublin", "deep-cleaning-dublin", "carpet-cleaning-dublin"],
  },
  "landlord-cleaning-dublin": {
    category: "residential", icon: Building2, name: "Landlord Cleaning",
    h1: "Landlord Cleaning Services Dublin",
    metaTitle: "Landlord Cleaning Services Dublin | GoCleaning Dublin",
    metaDescription: "Landlord cleaning services in Dublin. Cleaning between tenants and preparation for new occupants for landlords, estate agents and property managers. Invoices on request.",
    intro: "Landlord cleaning services in Dublin for cleaning between tenants and preparing properties for new occupants. We work with landlords, estate agents and property managers.",
    sections: [
      { h2: "How we help", items: ["Cleaning between tenants", "Preparation for new tenants and inspections", "For landlords, estate agents and property managers"] },
      { h2: "Flexible arrangements", items: ["Recurring arrangements available for multiple properties", "Invoices available on request for business billing", "Clear quotes confirmed before work begins"] },
    ],
    showPricing: "both",
    prefillServices: [], prefillCustomerType: "Landlord",
    related: ["end-of-tenancy-cleaning-dublin", "apartment-cleaning-dublin", "house-cleaning-dublin"],
  },
  "oven-cleaning-dublin": {
    category: "residential", icon: Flame, name: "Oven Cleaning",
    h1: "Oven Cleaning Dublin",
    metaTitle: "Oven Cleaning Dublin | GoCleaning Dublin",
    metaDescription: "Oven cleaning in Dublin. Internal oven, racks and trays cleaned of grease and burnt-on residue. Book alone or add to another cleaning service. Prices from €50.",
    intro: "Internal oven cleaning in Dublin, removing grease and burnt-on residue from the oven, racks and trays. Book it on its own or add it to another cleaning service.",
    sections: [
      { h2: "What's included", items: ["Internal oven cleaning", "Oven racks and trays", "Removal of grease and burnt-on residue"] },
      { h2: "How to book", items: ["Book oven cleaning as a standalone service", "Or add it to an end-of-tenancy or move-out clean"] },
    ],
    showPricing: "extras", extrasFilter: ["Oven cleaning"],
    prefillServices: ["Oven cleaning"],
    related: ["end-of-tenancy-cleaning-dublin", "deep-cleaning-dublin", "carpet-cleaning-dublin"],
  },
  "carpet-cleaning-dublin": {
    category: "residential", icon: Droplets, name: "Carpet Cleaning",
    h1: "Carpet Cleaning Dublin",
    metaTitle: "Carpet Cleaning Dublin | GoCleaning Dublin",
    metaDescription: "Carpet cleaning in Dublin for bedrooms, stairs, halls and living rooms. Pricing depends on room size and condition. Can be added to end-of-tenancy cleaning. Get a quote.",
    intro: "Carpet cleaning in Dublin for bedrooms, stairs, halls and living rooms. Pricing depends on the number and size of rooms and their condition.",
    sections: [
      { h2: "Rooms we clean", items: ["Bedrooms", "Stairs and halls", "Living rooms"] },
      { h2: "Pricing and booking", items: ["Priced by room size and condition", "Can be added to end-of-tenancy cleaning", "Send photos for an accurate quote"] },
    ],
    showPricing: "extras", extrasFilter: ["Carpet cleaning"],
    prefillServices: ["Carpet cleaning"],
    related: ["end-of-tenancy-cleaning-dublin", "house-cleaning-dublin", "deep-cleaning-dublin"],
  },
  "rubbish-removal-dublin": {
    category: "residential", icon: Trash2, name: "Rubbish & Furniture Removal",
    h1: "Rubbish and Furniture Removal Dublin",
    metaTitle: "Rubbish and Furniture Removal Dublin | GoCleaning Dublin",
    metaDescription: "Rubbish and furniture removal in Dublin. Unwanted bags, boxes, household items and furniture. Pricing depends on volume, weight, access and disposal. Send photos for a quote.",
    intro: "Rubbish and furniture removal in Dublin for unwanted bags, boxes, household items and furniture. Send photos of everything that needs to go and we'll quote it.",
    sections: [
      { h2: "What we can remove", items: ["Unwanted bags and boxes", "Household items", "Furniture — quoted after photos"] },
      { h2: "How pricing works", items: ["Send photos of everything to be removed", "Pricing depends on volume, weight, access and disposal requirements", "We confirm the price before collection"] },
    ],
    showPricing: "extras", extrasFilter: ["Rubbish removal", "Furniture removal"],
    prefillServices: ["Rubbish removal", "Furniture removal"],
    note: "We do not remove hazardous waste.",
    related: ["move-out-cleaning-dublin", "end-of-tenancy-cleaning-dublin"],
  },
  "office-cleaning-dublin": {
    category: "commercial", icon: Briefcase, name: "Office Cleaning",
    h1: "Office Cleaning Dublin",
    metaTitle: "Office Cleaning Dublin | GoCleaning Dublin",
    metaDescription: "Office cleaning in Dublin. Regular workplace cleaning, one-off deep cleaning and move-in/move-out office cleaning. Out-of-hours and recurring contracts available. Get a quote.",
    intro: "Office cleaning in Dublin for workplaces of all sizes. We offer regular workplace cleaning, one-off deep cleaning and move-in or move-out office cleaning.",
    sections: [
      { h2: "Office cleaning options", items: ["Regular workplace cleaning", "One-off deep cleaning", "Move-in and move-out office cleaning"] },
      { h2: "What we clean", items: ["Kitchens and staff areas", "Toilets", "Desks and common areas", "Floors"] },
      { h2: "Scheduling", items: ["Cleaning outside normal business hours, subject to availability", "Recurring weekly or scheduled cleaning contracts", "Invoices available on request"] },
    ],
    showPricing: false,
    prefillServices: ["Office cleaning"], prefillCustomerType: "Business",
    businessCta: true,
    related: ["commercial-cleaning-dublin", "business-cleaning-contracts-dublin"],
  },
  "commercial-cleaning-dublin": {
    category: "commercial", icon: Store, name: "Commercial Cleaning",
    h1: "Commercial Cleaning Dublin",
    metaTitle: "Commercial Cleaning Dublin | GoCleaning Dublin",
    metaDescription: "Commercial cleaning in Dublin for offices, shops, cafés, restaurants, salons, gyms, clinics and business premises. One-off, regular and contract cleaning. Get a quote.",
    intro: "Commercial cleaning in Dublin for a wide range of business premises. We provide one-off cleaning, regular weekly cleaning and scheduled cleaning contracts tailored to your needs.",
    sections: [
      { h2: "Premises we clean", items: ["Offices", "Retail shops", "Cafés and restaurants", "Salons and barbers", "Gyms", "Clinics and professional premises", "Apartment common areas", "Small warehouses and other business premises"] },
      { h2: "How we work", items: ["One-off commercial cleaning", "Regular weekly cleaning", "Scheduled cleaning contracts", "Customised cleaning plans", "Quotations based on property size, frequency and requirements"] },
    ],
    showPricing: false,
    prefillServices: ["Other business-premises cleaning"], prefillCustomerType: "Business",
    businessCta: true,
    note: "We do not provide specialist medical, hazardous, industrial or certified cleaning unless separately approved.",
    related: ["office-cleaning-dublin", "business-cleaning-contracts-dublin"],
  },
};

export function getService(slug) {
  return SERVICE_PAGES[slug] || null;
}

// Resolve pricing rows for a service page.
export function servicePricing(page) {
  if (!page.showPricing) return null;
  if (page.showPricing === "apartments") return { columns: [{ title: "Apartments", rows: PRICING.apartments }] };
  if (page.showPricing === "houses") return { columns: [{ title: "Houses", rows: PRICING.houses }] };
  if (page.showPricing === "extras") {
    const rows = PRICING.extras.filter((r) => (page.extrasFilter || []).includes(r.label));
    return { columns: [{ title: "Pricing", rows }] };
  }
  // both
  return { columns: [{ title: "Apartments", rows: PRICING.apartments }, { title: "Houses", rows: PRICING.houses }] };
}
